export type DashboardView = "current" | "income" | "todo";
export type DashboardFilters = {
  view: DashboardView;
  service: string;
  period: string;
  overdue?: boolean;
};
export type WorkQueue = { queue: string; count: number; oldest: string | null };
export type ServiceMetric = {
  kind: string;
  label: string;
  available: boolean;
  total_providers: number;
  live_providers: number;
  total_users: number;
  active_users: number;
  capacity_label: string | null;
  capacity: number;
  tasks: WorkQueue[];
  finance: { direction: string; category: string; amount_minor: number; occurred_at: string }[];
};
export type DashboardData = {
  services: ServiceMetric[];
  support_unread: number;
  generated_at: string;
  followup?: {
    overdue: { service: string; queue: string; count: number }[];
    receipts: { service: string; received_on: string; amount_minor: number }[];
  };
};
export const viewLabels = {
  current: "Current Status",
  income: "Income Status",
  todo: "Need to do",
};
export const serviceLabels: Record<string, string> = {
  hotel: "Link Stay",
  merchant: "Link Food",
  delivery_fleet: "Link Delivery",
  taxi_group: "Link Ride",
  wash: "Link Wash",
  night: "Link Night",
};
export const defaults: DashboardFilters = { view: "current", service: "all", period: "month" };
export const money = (minor: number) =>
  `${(minor / 100).toLocaleString("en-US", { maximumFractionDigits: 2 })} MMK`;
export function yangonDate(date = new Date()) {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Yangon",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
}
export function incomeTotals(services: ServiceMetric[], period: string, today: string) {
  const from = period === "today" ? today : period === "month" ? `${today.slice(0, 7)}-01` : "";
  const totals = {
    platform: 0,
    subscriptions: 0,
    promotions: 0,
    events: 0,
    provider: 0,
    sales: 0,
    manual: 0,
    expenses: 0,
    bookings: 0,
    cash: 0,
  };
  for (const service of services)
    for (const entry of service.finance) {
      if (entry.occurred_at < from || entry.occurred_at > today) continue;
      const amount = Number(entry.amount_minor);
      if (entry.direction === "expense") {
        if (["manual_expense", "refund"].includes(entry.category)) totals.expenses += amount;
        continue;
      }
      if (entry.category === "subscription_fee") totals.subscriptions += amount;
      if (entry.category === "event_fee") totals.events += amount;
      if (entry.category === "promotion_fee") totals.promotions += amount;
      if (entry.category === "service_sale") totals.sales += amount;
      if (entry.category === "manual_income") totals.manual += amount;
      if (entry.category === "booking_value") totals.bookings += amount;
      if (entry.category === "cash_collection") totals.cash += amount;
    }
  totals.platform = totals.subscriptions + totals.promotions + totals.events;
  totals.provider = totals.sales + totals.manual;
  return totals;
}
export function workLink(service: string, queue: string) {
  return `/more/operations?service=${service}&queue=${queue}`;
}
export function queueInfo(service: string, queue: string) {
  const provider =
    {
      hotel: "Hotel team",
      merchant: "Merchant team",
      delivery_fleet: "Fleet owner / rider",
      taxi_group: "Taxi team",
    }[service] ?? "Partner team";
  const definitions: Record<string, { title: string; owner: string; href: string }> = {
    applications: {
      title: "Partner applications",
      owner: "Admin",
      href: `/more/approvals?service=${service}`,
    },
    fees: {
      title: "Subscription fee approvals",
      owner: "Admin",
      href: "/more/subscription-requests",
    },
    payment_receipts: {
      title: "Outstanding fee payments",
      owner: "Admin / provider",
      href: "/finance",
    },
    event_applications: { title: "Event applications", owner: "Admin", href: "/more/events" },
    service_cases: {
      title: "Service cases",
      owner: "Admin / provider",
      href: "/more/service-help",
    },
    promotions: { title: "Promotion approvals", owner: "Admin", href: "/more/promotions" },
    renewals: {
      title: "Subscriptions due for review",
      owner: "Admin / partner",
      href: "/more/subscriptions",
    },
    verification: { title: "Unverified taxi drivers", owner: "Admin", href: "/more/taxi-drivers" },
    dispatch: { title: "Orders awaiting a fleet", owner: "Admin", href: "/orders" },
    unassigned: {
      title: "Jobs awaiting a rider",
      owner: "Fleet owner",
      href: workLink(service, queue),
    },
    pending: {
      title: service === "hotel" ? "Bookings awaiting confirmation" : "Orders awaiting a response",
      owner: provider,
      href: workLink(service, queue),
    },
    active: {
      title:
        service === "hotel"
          ? "Upcoming / current stays"
          : service === "delivery_fleet"
            ? "Active delivery jobs"
            : "Orders in progress",
      owner: provider,
      href: workLink(service, queue),
    },
  };
  return definitions[queue];
}
